import { useEffect, useRef, useState, useCallback } from 'react';
import { fetchBonusOutcome, type BonusRefreshReason } from './bonus-api';
import { NETWORK_UNAVAILABLE, type BonusView } from './bonus-state';
import { BONUS_AVAILABLE_IN_BUILD } from './bonus-availability';
import {
    BonusReadCoordinator, type BonusSnapshot,
} from './bonus-read-coordinator.ts';

/**
 * CF-BONUS Ticket B / SETTLED-UNAVAILABLE-RESILIENCE-V1 — the caller-level owner of bonus state.
 *
 * ⛔ This hook no longer contains the read logic. It is now a thin React adapter over
 * `BonusReadCoordinator`, which owns generations, sequences, both timers, the retry ladder and
 * the two latches. The previous `bonus-refresher.ts` is DELETED — leaving it would have left two
 * apparent owners of the same question, and tests could pass against the dead one.
 *
 * History worth keeping, because both bugs were the same shape — a lost event reads as "no bonus":
 *  - R2 shipped the surface never mounted; a player holding chips saw nothing.
 *  - R3's `if (inFlightRef.current) return;` DROPPED an event rather than deferring it, so a
 *    deposit that credited mid-read left the new bonus invisible. "Coalesce" means collapse many
 *    into ONE LATER read; it never means discard. One read suffices only if it BEGAN AFTER the
 *    event that changed the state.
 *
 * Not a polling loop: reads happen on lifecycle edges and the five state-changing events only.
 *
 * ⚠️ Flip settlement MUST stay its own trigger and cannot be inferred from a cash-balance change:
 * a fully chip-funded flip has `cash_gross = 0`, so cash does not move while turnover does.
 */

export interface BonusStateOwner {
    /** Current view. Starts `unavailable` — before the first read we genuinely do not know. */
    bonusView: BonusView;
    /**
     * False until the FIRST successful server answer. While false, `unavailable` means "still
     * establishing" and the UI renders NOTHING — rendering ignorance as an alarming card on first
     * paint put a flashing "temporarily unavailable" in front of every player (PL #12350).
     */
    bonusBootstrapped: boolean;
    /**
     * ⛔ The other half of the visibility predicate. True once the establishment budget is spent
     * with no answer ever received. Without it, a player whose first read never succeeds sees
     * silence forever — which is indistinguishable from "you have no bonus".
     */
    bonusInitialReadExhausted: boolean;
    /** Re-read on a state-changing event. Never throws; a failure resolves to `unavailable`. */
    refreshBonus: (reason: BonusRefreshReason) => void;
}

/**
 * @param sessionReady the backend session is usable. ⛔ Load-bearing: the coordinator issues ZERO
 * physical reads before this is true, so the mount read can no longer race auth and 401.
 */
export function useBonusState(apiBase: string, sessionReady: boolean): BonusStateOwner {
    const [snapshot, setSnapshot] = useState<BonusSnapshot>({
        phase: 'awaiting_session',
        view: NETWORK_UNAVAILABLE,
        bootstrapped: false,
        initialReadExhausted: false,
        eventGen: 0,
        seq: 0,
    });

    // One coordinator per hook instance. Created lazily so the ref initialiser never runs twice.
    const coordRef = useRef<BonusReadCoordinator | null>(null);
    if (coordRef.current === null) {
        coordRef.current = new BonusReadCoordinator({
            readOnce: fetchBonusOutcome,
            now: () => Date.now(),
            setTimer: (fn, ms) => setTimeout(fn, ms),
            clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
            publish: (s) => setSnapshot(s),
        });
    }

    // Activate/deactivate with the component. The coordinator's `activate()` CREATES its lifecycle
    // intent rather than looking for a stored one, so StrictMode's setup→cleanup→setup replay
    // cannot park the owner with no in-flight read and no queued replacement.
    useEffect(() => {
        if (!BONUS_AVAILABLE_IN_BUILD) return;
        const c = coordRef.current!;
        c.activate();
        return () => { c.deactivate(); };
    }, []);

    useEffect(() => {
        if (!BONUS_AVAILABLE_IN_BUILD) return;
        coordRef.current!.setBase(apiBase);
    }, [apiBase]);

    useEffect(() => {
        if (!BONUS_AVAILABLE_IN_BUILD) return;
        coordRef.current!.setSessionReady(sessionReady);
    }, [sessionReady]);

    const refreshBonus = useCallback((reason: BonusRefreshReason) => {
        // A build that does not run the programme never asks the server about it.
        if (!BONUS_AVAILABLE_IN_BUILD) return;
        // `mount`/`session_ready` are lifecycle edges the coordinator already owns; forwarding them
        // as events would spend a second token for a read it is going to make anyway.
        if (reason === 'mount' || reason === 'session_ready') return;
        // ⛔ NO CAST. The early return narrows `reason` to exactly the five event reasons, and
        // `notifyEvent` now accepts only `BonusEventReason` — so the compiler, not this comment,
        // is what guarantees a lifecycle edge can never reach the cooldown-gated door. The old
        // `as BonusReason` was the single construct that could have carried one through; deleting
        // it was the whole fix, and if this filter is ever weakened the build breaks here.
        coordRef.current!.notifyEvent(reason);
    }, []);

    return {
        bonusView: snapshot.view,
        bonusBootstrapped: snapshot.bootstrapped,
        bonusInitialReadExhausted: snapshot.initialReadExhausted,
        refreshBonus,
    };
}
